import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreateRestaurantTableRequest,
  CreateRestaurantZoneRequest,
  RestaurantTableDto,
  RestaurantZoneDto,
  UpdateRestaurantTableRequest,
  UpdateRestaurantZoneRequest,
} from './types';

const QK_ZONES = (branchId: string | null | undefined) =>
  ['restaurant-zones', branchId ?? 'none'] as const;
const QK_TABLES = (branchId: string | null | undefined) =>
  ['restaurant-tables', branchId ?? 'none'] as const;

// ---- Zones ----

export function useZones(branchId: string | null | undefined) {
  return useQuery({
    queryKey: QK_ZONES(branchId),
    enabled: !!branchId,
    queryFn: async () =>
      (
        await apiClient.get<RestaurantZoneDto[]>(
          `/api/restaurant/zones?branchId=${branchId}`,
        )
      ).data,
  });
}

export function useCreateZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateRestaurantZoneRequest) =>
      (await apiClient.post<RestaurantZoneDto>('/api/restaurant/zones', request))
        .data,
    onSuccess: (created) =>
      qc.invalidateQueries({ queryKey: QK_ZONES(created.branchId) }),
  });
}

export function useUpdateZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: string;
      request: UpdateRestaurantZoneRequest;
    }) =>
      (
        await apiClient.put<RestaurantZoneDto>(
          `/api/restaurant/zones/${id}`,
          request,
        )
      ).data,
    onSuccess: (updated) =>
      qc.invalidateQueries({ queryKey: QK_ZONES(updated.branchId) }),
  });
}

export function useDeleteZone(branchId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/restaurant/zones/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ZONES(branchId) }),
  });
}

// ---- Tables ----

export function useTables(branchId: string | null | undefined) {
  return useQuery({
    queryKey: QK_TABLES(branchId),
    enabled: !!branchId,
    queryFn: async () =>
      (
        await apiClient.get<RestaurantTableDto[]>(
          `/api/restaurant/tables?branchId=${branchId}`,
        )
      ).data,
  });
}

export function useCreateTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateRestaurantTableRequest) =>
      (await apiClient.post<RestaurantTableDto>('/api/restaurant/tables', request))
        .data,
    onSuccess: (created) =>
      qc.invalidateQueries({ queryKey: QK_TABLES(created.branchId) }),
  });
}

export function useUpdateTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: string;
      request: UpdateRestaurantTableRequest;
    }) =>
      (
        await apiClient.put<RestaurantTableDto>(
          `/api/restaurant/tables/${id}`,
          request,
        )
      ).data,
    onSuccess: (updated) =>
      qc.invalidateQueries({ queryKey: QK_TABLES(updated.branchId) }),
  });
}

export function useDeleteTable(branchId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/restaurant/tables/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TABLES(branchId) }),
  });
}
